import React, { useState, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCirclePlay,
  faCirclePause,
  faXmark,
  faChartSimple,
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import Chart from 'react-apexcharts';
import ResponsiveTable from './ResponsiveTable';
import { useMusicPlayer } from '../contexts/MusicPlayerContext';

const FALLBACK_IMG =
  'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/none_image.jpg';

function groupTopPlatforms(items, n = 7) {
  const sorted = [...items].sort((a, b) => b.platformAmount - a.platformAmount);
  if (sorted.length <= n) return sorted;
  const etcAmount = sorted.slice(n).reduce((s, r) => s + r.platformAmount, 0);
  return [...sorted.slice(0, n), { platformType: '기타', platformAmount: etcAmount }];
}

const PLATFORM_HORIZ_OPTIONS = (categories, id) => ({
  chart: { id, toolbar: { show: false }, background: 'transparent' },
  theme: { mode: 'dark' },
  plotOptions: { bar: { horizontal: true, borderRadius: 4, dataLabels: { position: 'end' } } },
  xaxis: { categories, labels: { show: false }, axisBorder: { show: false }, axisTicks: { show: false } },
  yaxis: { labels: { style: { fontSize: '12px', colors: '#94a3b8' }, maxWidth: 130 } },
  dataLabels: {
    enabled: true,
    formatter: (val) => val.toLocaleString(),
    offsetX: -6,
    style: { fontSize: '11px', colors: ['#94a3b8'] },
  },
  fill: { colors: ['#0ea5e9'] },
  grid: { borderColor: 'rgba(255,255,255,0.05)', padding: { left: 0, right: 70 } },
  tooltip: { theme: 'dark', y: { formatter: (val) => val.toLocaleString() } },
});

const UNIFIED_BAR_OPTIONS = (categories, id) => ({
  chart: { id, toolbar: { show: false }, background: 'transparent' },
  theme: { mode: 'dark' },
  xaxis: {
    categories,
    labels: { style: { fontSize: '12px', colors: '#94a3b8' } },
    axisBorder: { show: false },
    axisTicks: { show: false },
  },
  yaxis: {
    labels: { show: false },
    axisTicks: { show: false },
    axisBorder: { show: false },
  },
  dataLabels: {
    enabled: true,
    formatter: (val) => val.toLocaleString(),
    offsetY: -20,
    style: { fontSize: '11px', colors: ['#94a3b8'] },
  },
  plotOptions: {
    bar: { dataLabels: { position: 'top' }, borderRadius: 4 },
  },
  fill: { colors: ['#0ea5e9'] },
  grid: { borderColor: 'rgba(255,255,255,0.05)', padding: { left: 10, right: 0 } },
  tooltip: { theme: 'dark', y: { formatter: (val) => val.toLocaleString() } },
  responsive: [
    {
      breakpoint: 640,
      options: {
        chart: { height: 250, toolbar: { show: false } },
        yaxis: [{ labels: { show: false }, axisTicks: { show: false }, axisBorder: { show: false } }],
        grid: { padding: { left: 24 } },
      },
    },
  ],
});

const ChartLoading = () => (
  <div className="flex items-center justify-center gap-2 text-slate-400 text-sm py-16">
    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-sky-500" />
    <span>불러오는 중...</span>
  </div>
);

function ByClientMonthSongsTable({
  data = [],
  totalCount,
  currentPage,
  pageSize,
  month,
  clientId,
}) {
  const { playImmediate, playlist, currentIndex, isPlaying, togglePlay } = useMusicPlayer();
  const currentTrack = currentIndex !== null ? playlist[currentIndex] : null;
  const [selectedSong, setSelectedSong] = useState(null);
  const [monthlyAmounts, setMonthlyAmounts] = useState([]);
  const [loadingChart, setLoadingChart] = useState(false);
  const [selectedChannelSong, setSelectedChannelSong] = useState(null);
  const [platformAmounts, setPlatformAmounts] = useState([]);

  const fetchChartData = async (songId, clientId) => {
    if (!songId || !clientId || isNaN(clientId)) {
      console.error('필수 파라미터 누락:', { songId, clientId });
      return;
    }
    setLoadingChart(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/BySong/getMonthlyAmounts`,
        { params: { songId, clientId } }
      );
      setMonthlyAmounts(res.data);
    } catch (err) {
      console.error('정산 데이터 불러오기 오류:', err);
    } finally {
      setLoadingChart(false);
    }
  };

  const handleChannelClick = useCallback(
    async (song) => {
      if (!song?.songId) {
        console.warn('songId가 준비되지 않았습니다.');
        return;
      }

      setSelectedChannelSong(song);
      setPlatformAmounts([]);
      setLoadingChart(true);

      try {
        const monthYd = month?.slice(0, 7);
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/ByPlatform/byMonthPlatformsBySong`,
          { params: { songId: song.songId, monthYd, clientId } }
        );
        setPlatformAmounts(res.data.items || []);
      } catch (err) {
        console.error('플랫폼 정산 데이터 오류:', err);
      } finally {
        setLoadingChart(false);
      }
    },
    [month]
  );

  const columns = useMemo(
    () => [
      {
        Header: () => <div className="whitespace-nowrap text-center">순번</div>,
        id: 'index',
        accessor: (_row, i) => totalCount - ((currentPage - 1) * pageSize + i),
        Cell: ({ value }) => (
          <div className="text-center whitespace-nowrap">{value}</div>
        ),
      },
      {
        Header: '듣기',
        id: 'play',
        Cell: ({ row }) => {
          const song = row.original.song;
          const fileUrl = song?.fileUrl;
          const isCurrent = currentTrack && currentTrack.id === song?.id && isPlaying;

          const handlePlayClick = (e) => {
            e.stopPropagation();
            if (!fileUrl) return;
            if (currentTrack && currentTrack.id === song.id) {
              togglePlay();
              return;
            }
            playImmediate([{
              id: song.id,
              nameKr: song.nameKr,
              artistKr: song.artistKr || '',
              fileUrl: song.fileUrl,
              albumImage: FALLBACK_IMG,
              albumName: '',
            }]);
          };

          return fileUrl ? (
            <span
              className="inline-flex align-middle items-center justify-center cursor-pointer text-2xl"
              onClick={handlePlayClick}
              title={isCurrent ? '일시정지' : '재생'}>
              <FontAwesomeIcon
                icon={isCurrent ? faCirclePause : faCirclePlay}
                className={
                  isCurrent
                    ? 'text-red-400 hover:text-red-700'
                    : 'text-sky-500 hover:text-sky-700'
                }
              />
            </span>
          ) : (
            <span className="text-gray-400">-</span>
          );
        },
      },
      {
        Header: () => <div className="text-center whitespace-nowrap">채널</div>,
        id: 'channel',
        Cell: ({ row }) => (
          <span
            className="text-sky-500 text-xl cursor-pointer whitespace-nowrap"
            title="플랫폼별 정산 보기"
            onClick={() => handleChannelClick(row.original)}>
            <FontAwesomeIcon icon={faChartSimple} />
          </span>
        ),
      },
      {
        Header: '곡명',
        accessor: 'song.nameKr',
        Cell: ({ row }) => (
          <span
            className="font-bold text-sky-600 cursor-pointer"
            onClick={() => {
              const song = row.original;
              setSelectedSong(song);
              setMonthlyAmounts([]);
              fetchChartData(song.songId, clientId);
            }}>
            {row.original.song.nameKr}
          </span>
        ),
      },
      {
        Header: '아티스트',
        accessor: 'song.artistKr',
      },
      {
        Header: '총 히트수',
        accessor: 'songHitCount',
        Cell: ({ value }) => Number(value).toLocaleString(),
      },
      {
        Header: '총 정산액',
        accessor: 'finalAmount',
        Cell: ({ value }) => <b>{Number(value).toLocaleString()}</b>,
      },
    ],
    [totalCount, currentPage, pageSize, clientId, handleChannelClick, currentTrack, isPlaying, playImmediate, togglePlay]
  );

  return (
    <>
      <ResponsiveTable
        columns={columns}
        data={data}
        visibleOnMobile={['index', 'song.nameKr', 'finalAmount']}
      />

      {/* 월별 정산 모달 */}
      {selectedSong && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={() => setSelectedSong(null)}>
          <div
            className="bg-[#1a2b42] p-6 rounded-xl shadow-xl border border-white/10 w-full max-w-2xl relative"
            onClick={(e) => e.stopPropagation()}>
            <button
              className="absolute top-2 right-2 text-slate-400 hover:text-white"
              onClick={() => setSelectedSong(null)}>
              <FontAwesomeIcon icon={faXmark} size="lg" />
            </button>
            <h2 className="text-xl font-bold mb-4 text-white">
              "{selectedSong.song.nameKr}" 최근 1년 정산 그래프
            </h2>
            {loadingChart ? (
              <ChartLoading />
            ) : monthlyAmounts.length === 0 ? (
              <div className="flex items-center justify-center text-slate-400 text-sm py-16">
                정산 데이터 없음
              </div>
            ) : (
              <Chart
                type="bar"
                width="100%"
                height="300"
                series={[
                  {
                    name: '월별 정산액',
                    data: monthlyAmounts.map((item) => item.clientAmount),
                  },
                ]}
                options={UNIFIED_BAR_OPTIONS(
                  monthlyAmounts.map((item) => item.month),
                  'client-month-chart'
                )}
              />
            )}
          </div>
        </div>
      )}

      {/* 플랫폼 모달 */}
      {selectedChannelSong && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={() => setSelectedChannelSong(null)}>
          <div
            className="bg-[#1a2b42] p-6 rounded-xl shadow-xl border border-white/10 w-full max-w-2xl relative"
            onClick={(e) => e.stopPropagation()}>
            <button
              className="absolute top-2 right-2 text-slate-400 hover:text-white"
              onClick={() => setSelectedChannelSong(null)}>
              <FontAwesomeIcon icon={faXmark} size="lg" />
            </button>
            <h2 className="text-xl font-bold mb-4 text-white">
              "{selectedChannelSong.song.nameKr}" 플랫폼별 정산 그래프
            </h2>
            {loadingChart ? (
              <ChartLoading />
            ) : (() => {
              const grouped = groupTopPlatforms(platformAmounts);
              return (
                <Chart
                  type="bar"
                  width="100%"
                  height={Math.max(260, grouped.length * 42)}
                  series={[{ name: '정산액', data: grouped.map((item) => item.platformAmount) }]}
                  options={PLATFORM_HORIZ_OPTIONS(grouped.map((item) => item.platformType), 'platform-chart')}
                />
              );
            })()}
          </div>
        </div>
      )}
    </>
  );
}

export default ByClientMonthSongsTable;
